import { createFileRoute } from "@tanstack/react-router";
import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: "Politique de Confidentialité — angle-shop.com" },
      { name: "description", content: "Politique de confidentialité de angle-shop.com. Comment nous collectons, utilisons et protégeons vos données personnelles." },
    ],
  }),
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-3xl font-bold text-foreground mb-8">Privacy Policy</h1>
          
          <p className="text-muted-foreground leading-relaxed mb-8">
            angle-shop.com is committed to protecting the privacy and personal data of its users. 
            This privacy policy describes how we collect, use, store and protect your data.
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">1. Data Controller</h2>
            <div className="bg-muted p-4 rounded-lg mb-3">
              <p className="text-muted-foreground leading-relaxed">
                <strong>angle-shop.com</strong><br />
                Email: nessimselmane49@gmail.com
              </p>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              For any questions about your personal data or to exercise your rights, 
              you can contact us at the email address above.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">2. Data Collected</h2>
            
            <h3 className="text-lg font-medium text-foreground mb-3">2.1 Data collected during account creation or order</h3>
            <ul className="list-disc pl-5 text-muted-foreground mb-4 space-y-1">
              <li>First and last name</li>
              <li>Email address</li>
              <li>Postal address (shipping and billing)</li>
              <li>Phone number (optional)</li>
              <li>Order history</li>
            </ul>

            <h3 className="text-lg font-medium text-foreground mb-3">2.2 Automatically collected data</h3>
            <ul className="list-disc pl-5 text-muted-foreground mb-3 space-y-1">
              <li>IP address</li>
              <li>Browsing data (pages visited, time spent)</li>
              <li>Browser type and operating system</li>
              <li>Cookies and similar technologies (see Cookies section)</li>
            </ul>
            
            <p className="text-muted-foreground leading-relaxed">
              <strong>Important:</strong> We never store your banking information. 
              Payments are processed securely by our partner Stripe, PCI-DSS Level 1 certified. 
              angle-shop.com has no access to any credit card data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">3. Purpose of Processing</h2>
            <ul className="list-disc pl-5 text-muted-foreground mb-3 space-y-2">
              <li><strong>Order management:</strong> processing, shipping and tracking your orders</li>
              <li><strong>Customer service:</strong> responding to your questions and managing complaints</li>
              <li><strong>Payment:</strong> secure transaction processing</li>
              <li><strong>Delivery:</strong> transmitting your details to carriers</li>
              <li><strong>Communication:</strong> sending confirmation, tracking emails and newsletter (with consent)</li>
              <li><strong>Security:</strong> fraud detection and prevention</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">4. Data Retention</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-muted-foreground border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 pr-4 font-medium">Data Type</th>
                    <th className="text-left py-2 font-medium">Retention Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4">Account data</td>
                    <td className="py-2">Duration of relationship + 3 years of inactivity</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4">Order data</td>
                    <td className="py-2">10 years (accounting obligation)</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4">Payment data</td>
                    <td className="py-2">Managed exclusively by Stripe</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4">Session cookies</td>
                    <td className="py-2">Browser session</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Analytics cookies</td>
                    <td className="py-2">13 months maximum</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">5. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              You have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-5 text-muted-foreground mb-4 space-y-2">
              <li><strong>Right of access:</strong> obtain a copy of your personal data</li>
              <li><strong>Right of rectification:</strong> correct inaccurate or incomplete data</li>
              <li><strong>Right to erasure:</strong> request deletion of your data</li>
              <li><strong>Right to restriction:</strong> restrict the processing of your data</li>
              <li><strong>Right to portability:</strong> receive your data in a structured format</li>
              <li><strong>Right to object:</strong> object to processing, especially for marketing</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              To exercise these rights, contact us by email at <strong>nessimselmane49@gmail.com</strong>. 
              We will respond within a maximum of one month.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">6. Cookies</h2>
            <div className="space-y-3 mb-4">
              <div className="bg-muted p-4 rounded-lg">
                <p className="font-medium text-foreground mb-1">Essential cookies (required)</p>
                <p className="text-sm text-muted-foreground">
                  Necessary for the website to function: cart, user session, security. 
                  These cookies cannot be disabled.
                </p>
              </div>
              <div className="bg-muted p-4 rounded-lg">
                <p className="font-medium text-foreground mb-1">Performance & analytics cookies</p>
                <p className="text-sm text-muted-foreground">
                  Help us understand how visitors interact with the site 
                  (pages visited, time spent). These cookies are placed with your consent.
                </p>
              </div>
              <div className="bg-muted p-4 rounded-lg">
                <p className="font-medium text-foreground mb-1">Personalization cookies</p>
                <p className="text-sm text-muted-foreground">
                  Allow us to remember your preferences (language, persistent cart).
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">7. Data Security</h2>
            <ul className="list-disc pl-5 text-muted-foreground mb-3 space-y-1">
              <li>SSL/TLS encryption for all communications (HTTPS)</li>
              <li>Secure hosting with certified cloud partners</li>
              <li>Restricted access to personal data for authorized personnel only</li>
              <li>Regular encrypted backups</li>
              <li>Payment security via Stripe (PCI-DSS Level 1 certification)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">8. Contact</h2>
            <div className="bg-muted p-6 rounded-lg">
              <p className="text-muted-foreground leading-relaxed mb-4">
                For any questions about this privacy policy or the processing of your data:
              </p>
              <p className="text-muted-foreground leading-relaxed">
                <strong>angle-shop.com</strong><br />
                Email: nessimselmane49@gmail.com
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                Last updated: April 15, 2026
              </p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
